import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { API } from '../api';
import { AuthService } from 'src/app/security/auth.service';

export interface ComunicacaoHistoricoItem {
  id: number;
  titulo: string;
  tipoMensagem: number;
  canal: number;
  dataEnvio: string;
  envioComSucesso: boolean;
  tentativa: number;
  regraNome: string | null;
}

export interface HistoricoPagedResponse {
  data: ComunicacaoHistoricoItem[];
  total: number;
  pagina: number;
  tamanhoPagina: number;
}

export interface HistoricoFiltroParams {
  canal?: number;
  busca?: string;
  take?: number;
  skip?: number;
}

export interface DashboardResumoDto {
  totalEnviados: number;
  totalPendentes: number;
  totalErros: number;
  porCanal: { canal: string; tipoMensagem: number; enviados: number; erros: number; pendentes: number }[];
  cacheGeradoEm: string;
}

export interface DashboardItemDto {
  alunoId: number;
  nomeAluno: string;
  matricula: string;
  titulo: string;
  tipoMensagem: number;
  canal: number;
  dataEnvio: string;
  envioComSucesso: boolean;
  tentativa: number;
  regraNome: string | null;
  offsetDias: number | null;
}

export interface DashboardItemsPagedResponse {
  data: DashboardItemDto[];
  total: number;
  pagina: number;
  tamanhoPagina: number;
}

export interface DashboardFiltro {
  de: string;
  ate: string;
  unidadeId?: number;
}

export interface DashboardFiltroCompleto extends DashboardFiltro {
  canal?: number;
  busca?: string;
  status?: string;
  orderBy?: string;
  orderDesc?: boolean;
  pagina?: number;
  tamanhoPagina?: number;
  filtroNome?: string;
  filtroTitulo?: string;
  filtroRegra?: string;
  filtroOffsetDias?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ComunicacaoService {

  constructor(private http: HttpClient, private authService: AuthService) { }

  getHistoricoAluno(alunoId: number, params: HistoricoFiltroParams): Observable<HistoricoPagedResponse> {
    const usuario = this.authService.getToken();
    const headers = new HttpHeaders().set('Authorization', 'Bearer ' + usuario?.token);
    let httpParams = new HttpParams();
    if (params.canal != null) httpParams = httpParams.set('canal', params.canal.toString());
    if (params.busca) httpParams = httpParams.set('busca', params.busca);
    if (params.take != null) httpParams = httpParams.set('take', params.take.toString());
    if (params.skip != null) httpParams = httpParams.set('skip', params.skip.toString());

    return this.http.get<HistoricoPagedResponse>(
      `${environment.baseUrl}${API.reguaContato.historico}/${alunoId}`,
      { params: httpParams, headers }
    );
  }

  getDashboardResumo(filtro: DashboardFiltro): Observable<DashboardResumoDto> {
    const usuario = this.authService.getToken();
    const headers = new HttpHeaders().set('Authorization', 'Bearer ' + usuario?.token);
    let httpParams = new HttpParams()
      .set('de', filtro.de)
      .set('ate', filtro.ate);
    if (filtro.unidadeId != null) httpParams = httpParams.set('unidadeId', filtro.unidadeId.toString());

    return this.http.get<DashboardResumoDto>(
      `${environment.baseUrl}${API.comunicacaoDashboard.resumo}`,
      { params: httpParams, headers }
    );
  }

  getDashboardItems(filtro: DashboardFiltroCompleto): Observable<DashboardItemsPagedResponse> {
    const usuario = this.authService.getToken();
    const headers = new HttpHeaders().set('Authorization', 'Bearer ' + usuario?.token);
    let httpParams = new HttpParams()
      .set('de', filtro.de)
      .set('ate', filtro.ate);
    if (filtro.unidadeId != null) httpParams = httpParams.set('unidadeId', filtro.unidadeId.toString());
    if (filtro.canal != null) httpParams = httpParams.set('canal', filtro.canal.toString());
    if (filtro.busca) httpParams = httpParams.set('busca', filtro.busca);
    if (filtro.status) httpParams = httpParams.set('status', filtro.status);
    if (filtro.orderBy) httpParams = httpParams.set('orderBy', filtro.orderBy);
    if (filtro.orderDesc != null) httpParams = httpParams.set('orderDesc', filtro.orderDesc.toString());
    if (filtro.pagina != null) httpParams = httpParams.set('pagina', filtro.pagina.toString());
    if (filtro.tamanhoPagina != null) httpParams = httpParams.set('tamanhoPagina', filtro.tamanhoPagina.toString());
    if (filtro.filtroNome) httpParams = httpParams.set('filtroNome', filtro.filtroNome);
    if (filtro.filtroTitulo) httpParams = httpParams.set('filtroTitulo', filtro.filtroTitulo);
    if (filtro.filtroRegra) httpParams = httpParams.set('filtroRegra', filtro.filtroRegra);
    if (filtro.filtroOffsetDias != null) httpParams = httpParams.set('filtroOffsetDias', filtro.filtroOffsetDias.toString());

    return this.http.get<DashboardItemsPagedResponse>(
      `${environment.baseUrl}${API.comunicacaoDashboard.items}`,
      { params: httpParams, headers }
    );
  }

  exportarCSV(filtro: DashboardFiltroCompleto): Observable<Blob> {
    const usuario = this.authService.getToken();
    const headers = new HttpHeaders().set('Authorization', 'Bearer ' + usuario?.token);
    let httpParams = new HttpParams()
      .set('de', filtro.de)
      .set('ate', filtro.ate);
    if (filtro.unidadeId != null) httpParams = httpParams.set('unidadeId', filtro.unidadeId.toString());
    if (filtro.canal != null) httpParams = httpParams.set('canal', filtro.canal.toString());
    if (filtro.busca) httpParams = httpParams.set('busca', filtro.busca);
    if (filtro.status) httpParams = httpParams.set('status', filtro.status);
    if (filtro.orderBy) httpParams = httpParams.set('orderBy', filtro.orderBy);
    if (filtro.orderDesc != null) httpParams = httpParams.set('orderDesc', filtro.orderDesc.toString());
    if (filtro.filtroNome) httpParams = httpParams.set('filtroNome', filtro.filtroNome);
    if (filtro.filtroTitulo) httpParams = httpParams.set('filtroTitulo', filtro.filtroTitulo);
    if (filtro.filtroRegra) httpParams = httpParams.set('filtroRegra', filtro.filtroRegra);
    if (filtro.filtroOffsetDias != null) httpParams = httpParams.set('filtroOffsetDias', filtro.filtroOffsetDias.toString());

    return this.http.get(
      `${environment.baseUrl}${API.comunicacaoDashboard.exportar}`,
      { params: httpParams, responseType: 'blob', headers }
    );
  }
}
